/**
 * Template de prise de besoin : ODS – Support & gestion TI
 * Basé sur l'exemple ODS_2025_MSA
 */

import type { IntakeTemplate } from '../templateTypes';

const DEFAULT_INCLUSIONS_DISTINCTIFS = `| Services distinctifs | Inclus | Non inclus | Notes |
| --- | --- | --- | --- |
| Accès à une équipe multidisciplinaire intégrée | ✔️ |  |  |
| Directeur de compte dédié | ✔️ |  |  |
| Portail Mon BZ (communications & info d'affaires) | ✔️ |  |  |
| Accès à 10 formations d'introduction Talamus | ✔️ |  |  |
| Événement privé vision TI (spécialistes TI/cyber) | ✔️ |  |  |
| Événement de réseautage d'affaires | ✔️ |  |  |
| Webinaire périodique (sujets TI) | ✔️ |  |  |
| Communications BZ périodiques | ✔️ |  |  |`;

const DEFAULT_INCLUSIONS_INFOGERANCE = `| Services gérés (infogérance) | Inclus | Non inclus | Notes |
| --- | --- | --- | --- |
| Support technique 7h-17h (soutien) | ✔️ |  |  |
| Support technique 7h-17h (expertise) | ✔️ |  |  |
| Hotline support rapide | ✔️ |  |  |
| Urgence 24/7 (selon lexique) | ✔️ |  |  |
| SWAT sécurité (heures dédiées) | ✔️ |  |  |
| SWAT techno (heures dédiées) | ✔️ |  |  |
| Gestion M365 (licences, config, support) | ✔️ |  |  |
| Mises à jour et correctifs | ✔️ |  |  |
| Installation planifiée d'équipements | ✔️ |  |  |
| Dépannage systèmes/logiciels | ✔️ |  |  |
| Surveillance systèmes supportés | ✔️ |  |  |
| Rapport d'incident cybersécurité (si requis) | ✔️ |  |  |`;

const DEFAULT_INCLUSIONS_CLOUD = `| Infonuagique / sauvegardes | Inclus | Non inclus | Notes |
| --- | --- | --- | --- |
| Sauvegarde M365 (Sherweb) | ✔️ |  |  |
| Sauvegarde environnement local (BZ) |  | ❌ |  |
| Archivage hors ligne (physique client) |  | ❌ |  |
| VM (CPU, RAM, stockage) | ✔️ |  |  |
| Pare-feu virtuel de base | ✔️ |  |  |
| Pare-feu virtuel avancé | ✔️ |  |  |
| Archivage trimestriel hors-ligne | ✔️ |  |  |
| 1 IP publique (env. dédié requis) | ✔️ |  |  |`;

const DEFAULT_INCLUSIONS_EQUIP = `| Équipements / logiciels / licences | Inclus | Non inclus | Notes |
| --- | --- | --- | --- |
| Conseils & achats équipements | ✔️ |  |  |
| Gestion des garanties | ✔️ |  |  |
| Licences M365 (selon choix client) | ✔️ |  |  |
| Licence Copilot |  | ❌ |  |
| Licence Duo (2FA) | ✔️ |  |  |
| Fortimanager |  | ❌ |  |
| Field Effect (MDR) |  | ❌ |  |
| LastPass |  | ❌ |  |
| Exclaimer |  | ❌ |  |`;

const DEFAULT_TARIFICATION = `| Catégorie | Item | Prix unitaire | Unités | Total |
| --- | --- | --- | --- | --- |
| Infogérance | BZ Support Mobile | 25,00 $ | 2 | 50,00 $ |
| Infogérance | BZ Support Premium | 125,00 $ | 1 | 125,00 $ |
| Microsoft 365 | M365 Business Premium | 31,29 $ | 3 | 93,87 $ |
| Microsoft 365 | Sauvegarde M365 Sherweb | 4,00 $ | 3 | 12,00 $ |
| Licences admin | Licence Administrateur BZ | 9,72 $ | 1 | 9,72 $ |
| BZ Cloud | Machines virtuelles | 6,05 $ | 1 | 6,05 $ |
| BZ Cloud | Processeurs | 32,10 $ | 3 | 96,30 $ |
| BZ Cloud | RAM provisionnée | 6,80 $ | 16 | 108,80 $ |
| BZ Cloud | Espace disque provisionné | 0,16 $ | 800 | 128,00 $ |
| Licences SPLA | CAL Terminal Serveur | 11,36 $ | 3 | 34,08 $ |
| Sécurité | Authentification 2FA (Duo) | 10,00 $ | 3 | 30,00 $ |`;

/** Tableau vierge pour un produit additionnel (mensualité) */
export const DEFAULT_PRODUIT_TABLE = `| Catégorie | Item | Prix unitaire | Unités | Total |
| --- | --- | --- | --- | --- |
|  |  |  |  |  |`;

export const templateOdsSupportGestion: IntakeTemplate = {
  id: 'ods-support-gestion',
  name: 'Prise de besoin - ODS',
  description:
    'Questionnaire complet pour produire une Offre de Service (ODS) alignée sur les besoins du client, incluant catalogue de services, SLA et tarification.',
  documentTypeCode: 'ODS',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Offre commerciale structurée',
      description: 'Introduction, contexte client, recommandations et plan de travail',
      icon: 'Building2',
      questions: [
        {
          id: 'company_name',
          label: "Nom de l'entreprise",
          type: 'text',
          required: true,
        },
        {
          id: 'proposal_date',
          label: 'Date de proposition',
          type: 'date',
          required: true,
        },
        {
          id: 'account_manager',
          label: 'Directeur de compte',
          type: 'text',
          required: true,
        },
        {
          id: 'proposal_city',
          label: 'Ville / lieu',
          type: 'text',
          required: false,
        },
        {
          id: 'context_summary',
          label: 'Résumé du contexte client',
          type: 'textarea',
          placeholder: 'Contexte global, enjeux clés et attentes du client.',
          required: true,
        },
        {
          id: 'firme_ti',
          label: 'Firme TI',
          type: 'select',
          options: ['Oui', 'Non'],
          required: false,
          followUp: {
            whenValue: 'Oui',
            id: 'firme_ti_nom',
            placeholder: 'Nom de la firme TI',
            type: 'text',
          },
        },
        {
          id: 'needs_highlights',
          label: 'Problématiques principales identifiées',
          type: 'multiselect',
          options: [
            'Migration courriels / collaboration',
            'Modernisation applicative',
            'Sauvegardes / continuité',
            'Sécurité / conformité (Loi 25)',
            'Serveur obsolète',
            'Support TI interne insuffisant',
            'Autre',
          ],
          required: true,
        },
        {
          id: 'recommendations',
          label: 'Recommandations proposées',
          type: 'textarea',
          placeholder: 'Liste des recommandations concrètes, ordonnées.',
          required: true,
        },
        {
          id: 'plan_implanter',
          label: 'Plan de travail – Implanter',
          type: 'multiselect',
          options: [
            'Migration de vos données vers l\'environnement Microsoft 365.',
            'Migration de vos courriels vers l\'environnement Microsoft 365.',
            'Implantation des outils de sécurité inclus avec votre licence Business Premium.',
            'Documentation du plan de votre infrastructure et de vos procédures.',
            'Accompagnement pour une stratégie de formation.',
            'Optimiser l\'utilisation des ressources infonuagiques.',
            'Prise en charge complète de votre parc informatique.',
          ],
          required: true,
          subOptions: {
            'Implantation des outils de sécurité inclus avec votre licence Business Premium.': [
              'Déploiement d\'Intune pour la gestion de vos périphériques.',
              'Déploiement de l\'antivirus EDR.',
              'Déploiement de l\'antispam avancé.',
              'Déploiement des autres stratégies de sécurité discutées.',
            ],
            'Accompagnement pour une stratégie de formation.': [
              'Formation de base sur Microsoft 365 par BZ.',
              'Formations avancées via le programme Talamus.',
            ],
          },
        },
        {
          id: 'plan_gerer',
          label: 'Plan de travail – Gérer',
          type: 'multiselect',
          helpText:
            'La deuxième phase implique un suivi rigoureux et la maintenance de vos systèmes de manière optimale incluant les opérations de mise à jour de l\'ensemble des outils et logiciels.',
          options: [
            'Gestion et validation quotidienne des sauvegardes avec tableau de suivi.',
            'Archivage hors ligne.',
            'Mises à jour régulières de vos postes de travail (outils Microsoft).',
            'Veille technologique sur l\'amélioration de la posture de sécurité.',
            'Améliorations et mises à jour des infrastructures serveurs.',
          ],
          required: true,
        },
        {
          id: 'plan_optimiser',
          label: 'Plan de travail – Optimiser',
          type: 'multiselect',
          helpText:
            'Cette phase vise à optimiser votre situation à long terme, nous adoptons une approche proactive axée sur la stabilité, la cyber-résistance et la performance de vos systèmes. Selon les options retenues, nous allons élaborer un plan directeur annuel détaillé d\'une portée variant d\'une (1) à cinq (5) ans, un plan de formation personnalisé ainsi qu\'un programme adapté en cybersécurité.',
          options: [
            'Plan directeur annuel.',
            'Évaluer la possibilité de remplacer des logiciels traditionnels par des applications Web.',
            'Accompagnement lors de vos projets d\'entreprise (déménagement, gestion de croissance, nouveaux logiciels, etc.).',
          ],
          required: true,
          subOptions: {
            'Plan directeur annuel.': [
              'Budgétaire 1 à 5 ans.',
              'Mise en place des nouveaux outils.',
              'Revue de l\'année précédente.',
              'Améliorations diverses.',
            ],
          },
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Catalogue de services',
      description: 'Inclusions et exclusions, tableaux ajustables',
      icon: 'FileText',
      questions: [
        {
          id: 'catalog_inclusions_distinctifs',
          label: 'Inclusions TI – Services distinctifs',
          type: 'textarea',
          helpText: 'Tableau Markdown modifiable (| colonnes |).',
          defaultValue: DEFAULT_INCLUSIONS_DISTINCTIFS,
          required: true,
        },
        {
          id: 'catalog_inclusions_infogerance',
          label: 'Inclusions TI – Services gérés (infogérance)',
          type: 'textarea',
          helpText: 'Tableau Markdown modifiable (| colonnes |).',
          defaultValue: DEFAULT_INCLUSIONS_INFOGERANCE,
          required: true,
        },
        {
          id: 'catalog_inclusions_cloud',
          label: 'Inclusions TI – Infonuagique & sauvegardes',
          type: 'textarea',
          helpText: 'Tableau Markdown modifiable (| colonnes |).',
          defaultValue: DEFAULT_INCLUSIONS_CLOUD,
          required: true,
        },
        {
          id: 'catalog_inclusions_equip',
          label: 'Inclusions TI – Équipements, logiciels et licences',
          type: 'textarea',
          helpText: 'Tableau Markdown modifiable (| colonnes |).',
          defaultValue: DEFAULT_INCLUSIONS_EQUIP,
          required: true,
        },
        {
          id: 'catalog_notes',
          label: 'Notes et ajustements au catalogue',
          type: 'textarea',
          placeholder: 'Ajouts, exclusions ou particularités spécifiques.',
          required: false,
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Tarification détaillée',
      description: 'Tableaux de prix modifiables',
      icon: 'Target',
      questions: [
        {
          id: 'pricing_table',
          label: 'Tableau de tarification (modifiable)',
          type: 'textarea',
          helpText: 'Tableau Markdown modifiable (| colonnes |).',
          defaultValue: DEFAULT_TARIFICATION,
          required: true,
        },
        {
          id: 'monthly_total',
          label: 'Mensualité TI totale',
          type: 'text',
          placeholder: 'Ex: 693,82 $',
          required: false,
        },
        {
          id: 'one_time_fees',
          label: 'Frais uniques (migration, implantation, etc.)',
          type: 'textarea',
          placeholder: 'Ex: Frais de migration unique 800,00 $',
          required: false,
        },
        {
          id: 'pricing_notes',
          label: 'Notes sur la tarification',
          type: 'textarea',
          placeholder: 'Ex: prix dégressifs, ajustements selon licences, etc.',
          required: false,
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Autres produits',
      description: "Produits additionnels (cybersécurité, téléphonie, programmation, solutions d'affaires) avec grilles de prix",
      icon: 'Briefcase',
      questions: [
        {
          id: 'autres_produits',
          label: 'Autres produits',
          type: 'textarea',
          helpText: 'Type de produit → Ajouter → Titre du produit et tableau mensualité / frais de migration.',
          defaultValue: '[]',
          required: false,
        },
      ],
    },
    {
      id: 'phase5',
      title: 'Annexes pédagogiques',
      description: 'Lexique, expertise BZ, programmes et annexes',
      icon: 'Briefcase',
      questions: [
        {
          id: 'expertise_sections',
          label: 'Sections d’expertise à inclure',
          type: 'multiselect',
          options: [
            'Infogérance TI',
            'Infonuagique',
            'Cybersécurité',
            'Programmation',
            'Téléphonie',
            'Équipements & licences',
          ],
          required: false,
        },
        {
          id: 'programs_to_include',
          label: 'Programmes spécialisés à inclure',
          type: 'multiselect',
          options: [
            'Programme cybersécurité (préliminaire)',
            'Programme Loi 25',
            'Examen de conformité',
            'Formations Microsoft 365',
          ],
          required: false,
        },
        {
          id: 'annexes_notes',
          label: 'Notes complémentaires',
          type: 'textarea',
          placeholder: 'Ajouts ou adaptations spécifiques à inclure en annexe.',
          required: false,
        },
      ],
    },
  ],
};
